import clsx from "clsx";
import React, { Fragment, FunctionComponent, ReactNode } from "react";

import { useLanguage } from "../../i18n/useLanguage";
import { useBodyClass } from "../../util/useBodyClass";

type ContentEntry = [string, ReactNode[], string, [string, string]];

type Content = {
  en: ContentEntry[];
  fr: ContentEntry[];
};

const content: Content = {
  en: [
    [
      "The right tires make a difference",
      [
        "Mazda approved tires are essential for maintaining your vehicle’s peak performance. The quality of your ride and overall driving experience hinges significantly on your tires, and not all meet our stringent standards. Each original equipment tire has earned Mazda’s highest rating, making it the ideal choice for your specific model and driving requirements. Whether you require all-season or winter, you can rely on us to help you discover the perfect tire match for your needs.",
      ],
      "/static/img/mazda_v2/homepage/rigt_tires.jpg",
      ["md:order-2", "md:order-1"],
    ],
    [
      "Shop Genuine Mazda Accessories",
      [
        "Personalizing your Mazda to suit your lifestyle is easy. Our extensive selection of interior and exterior accessories combines functionality and style – and because we only offer Genuine Mazda Accessories, you’ll have the perfect fit every time",
        "Genuine Mazda Accessories provide the style, comfort, quality, and satisfaction you expect from Mazda. They are meticulously crafted and guaranteed to seamlessly integrate with your vehicle’s interior and exterior. These accessories adhere to the same rigorous quality standards as your Mazda and are backed by our warranty.",
      ],
      "/static/img/mazda_v2/homepage/wide-selection.jpg",
      ["md:order-3", "md:order-4"],
    ],
    [
      "Perfect Genuine Alloy Wheels for Your Mazda",
      [
        "We recognize that each car has its own unique personality, much like its owner. That’s why we offer you the opportunity to personalize your Mazda with alloy wheels that perfectly complement your personal style.",
        "These wheels are hub-centric and seamlessly compatible with the original hardware and tire pressure monitoring sensors. The hardest part will be deciding which meticulously designed wheel to install on your Mazda.",
      ],
      "/static/img/mazda_v2/homepage/all-season.jpg",
      ["md:order-6", "md:order-5"],
    ],
    [
      "Wide Selection of Tires",
      [
        <div className="grid gap-3" key="wt">
          <p>
            Discover the perfect combination of value and performance with our
            selection of tires designed specifically for your Mazda. Use our
            Tire Selector tool to find the recommended tires and sizes for your
            vehicle.
          </p>
          <div>
            <p className="font-bold">All-Season Tires:</p>
            <p>
              Most Mazda models come standard with “All Season” tires, offering:
            </p>
            <ul className="list-disc pl-4">
              <li>Responsive performance in various conditions</li>
              <li>Durable tread life</li>
              <li>Comfortable and quiet driving experience</li>
            </ul>
          </div>
          <div>
            <p className="font-bold">Winter Tires:</p>
            <p>
              From temperatures of 7°C and below, summer and all-season tires
              may no longer provide optimum grip as their rubber compound can
              harden, essentially causing them to slide like a hockey puck on
              the ice. By contrast, Mazda-Approved, cold weather, winter tires
              have been specially designed to perform in these conditions.
            </p>
            <ul className="list-disc pl-4">
              <li>Winter tires have better grip in cold weather.</li>
              <li>
                They help your vehicle perform in all types of challenging
                winter conditions – snow, ice, sleet, slush, wet and even cold,
                dry roads.
              </li>
              <li>
                We recommend switching to Winter Tires when average daily
                temperatures drop below 7°C.
              </li>
            </ul>
          </div>
        </div>,
      ],
      "/static/img/mazda_v2/homepage/winter-tires.jpg",
      ["md:order-7", "md:order-8"],
    ],
  ],
  fr: [
    [
      "Les bons pneus font la différence",
      [
        "Les pneus approuvés par Mazda sont essentiels pour maintenir les performances optimales de votre véhicule. La qualité de votre conduite et de votre expérience de conduite dépendent considérablement de vos pneus, et tous ne répondent pas à nos normes strictes. Chaque pneu d'équipement d'origine a obtenu la plus haute note de Mazda, ce qui en fait le choix idéal pour votre modèle spécifique et vos besoins de conduite. Que vous ayez besoin de pneus toutes saisons ou d'hiver, vous pouvez compter sur nous pour vous aider à découvrir le pneu parfait pour vos besoins.",
      ],
      "/static/img/mazda_v2/homepage/rigt_tires.jpg",
      ["md:order-2", "md:order-1"],
    ],
    [
      "Achetez des accessoires Mazda authentiques",
      [
        "Personnaliser votre Mazda pour qu'elle corresponde à votre style de vie est facile. Notre vaste sélection d'accessoires intérieurs et extérieurs combine fonctionnalité et style – et comme nous n'offrons que des Accessoires Mazda Authentiques, vous aurez l'ajustement parfait à chaque fois",
        "Les Accessoires Mazda Authentiques offrent le style, le confort, la qualité et la satisfaction que vous attendez de Mazda. Ils sont méticuleusement conçus et garantis pour s'intégrer parfaitement à l'intérieur et à l'extérieur de votre véhicule. Ces accessoires respectent les mêmes normes de qualité rigoureuses que votre Mazda et sont soutenus par notre garantie.",
      ],
      "/static/img/mazda_v2/homepage/wide-selection.jpg",
      ["md:order-3", "md:order-4"],
    ],
    [
      "Jantes en alliage authentiques parfaites pour votre Mazda",
      [
        "Nous reconnaissons que chaque voiture a sa propre personnalité unique, tout comme son propriétaire. C'est pourquoi nous vous offrons la possibilité de personnaliser votre Mazda avec des jantes en alliage qui complètent parfaitement votre style personnel.",
        "Ces roues sont centrées sur le moyeu et compatibles sans problème avec le matériel d'origine et les capteurs de surveillance de la pression des pneus. La partie la plus difficile sera de décider quelle roue méticuleusement conçue installer sur votre Mazda.",
      ],
      "/static/img/mazda_v2/homepage/all-season.jpg",
      ["md:order-6", "md:order-5"],
    ],
    [
      "Large sélection de pneus",
      [
        <div className="grid gap-3" key="wt">
          <p>
            Découvrez la combinaison parfaite de valeur et de performance avec
            notre sélection de pneus conçus spécifiquement pour votre Mazda.
            Utilisez notre outil Sélecteur de pneus pour trouver les pneus et
            les tailles recommandés pour votre véhicule.
          </p>
          <div>
            <p className="font-bold">Pneus toutes saisons :</p>
            <p>
              La plupart des modèles Mazda sont équipés de série de pneus
              &quot;toutes saisons&quot;, offrant :
            </p>
            <ul className="list-disc pl-4">
              <li>Performance réactive dans diverses conditions</li>
              <li>Durée de vie de la bande de roulement durable</li>
              <li>Expérience de conduite confortable et silencieuse</li>
            </ul>
          </div>
          <div>
            <p className="font-bold">Pneus d&apos;hiver :</p>
            <p>
              À partir de températures de 7°C et en dessous, les pneus
              d&apos;été et toutes saisons peuvent ne plus offrir une adhérence
              optimale car leur composé de caoutchouc peut durcir, les faisant
              glisser comme un palet de hockey sur la glace. En contraste, les
              pneus d&apos;hiver approuvés par Mazda, conçus pour le temps
              froid, ont été spécialement conçus pour performer dans ces
              conditions.
            </p>
            <ul className="list-disc pl-4">
              <li>
                Les pneus d&apos;hiver ont une meilleure adhérence par temps
                froid.
              </li>
              <li>
                Ils aident votre véhicule à performer dans tous les types de
                conditions hivernales difficiles – neige, glace, grésil, neige
                fondue, routes mouillées et même sèches et froides.
              </li>
              <li>
                Nous recommandons de passer aux pneus d&apos;hiver lorsque les
                températures moyennes journalières descendent en dessous de 7°C.
              </li>
            </ul>
          </div>
        </div>,
      ],
      "/static/img/mazda_v2/homepage/winter-tires-fr.jpg",
      ["md:order-7", "md:order-8"],
    ],
  ],
};

export const MazdaHomePage: FunctionComponent = () => {
  const [language] = useLanguage();
  const entries = content[language];
  useBodyClass("landing-page");
  return (
    <section className="grid md:grid-cols-2 my-8 md:mt-16 lg:mt-24">
      {entries.map(([title, description, image, [imgClass, textClass]]) => {
        return (
          <Fragment key={image}>
            <img
              className={clsx(
                "object-cover object-center h-full w-full aspect-[4/3]",
                imgClass
              )}
              alt={title}
              src={image}
            />
            <div
              className={clsx("flex items-center justify-center", textClass)}
            >
              <div className="p-6 max-w-[40rem]">
                <h2 className="text-xl lg:text-3xl uppercase mb-2 md:mb-4">
                  {title}
                </h2>
                {description.map((paragraph, key) => (
                  <p className={clsx(key && "mt-2 md:mt-4")} key={key}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </Fragment>
        );
      })}
    </section>
  );
};
